import classNames from "classnames";
import { useCallback, useState, type FC } from "react";
import { useTranslation } from "react-i18next";
import { useNotesTable, useTabsManager } from "@/features/NotesManager/ui/components/desktop/hooks";
import {
  DEFAULT_NOTE_TYPES,
  useCreateNoteMutation,
  useGetAllPersonsQuery,
  useUpdateNoteMutation,
} from "@/services/api";
import {
  createNewTempNote,
  prepareCreateNotePayload,
  prepareUpdateNotePayload,
} from "@/features/NotesManager/helpers";
import { useAppDispatch, useAppSelector } from "@/services/store";
import { AddNoteButton } from "@/features/NotesManager/Components/AddNoteButton";
import { NoteEditorMobile } from "@/features/NoteEditor/Components/Layout/mobile";
import { AppDrawer } from "@/shared/Components/AppDrawer";
import { getTableCardMobileContent } from "@/shared/utils/get-mobile-content";
import { MobileHeaderTitle } from "@/common/AppMobileHeader";
import { DeleteNotesModal } from "@/features/NotesManager/ui/components/desktop/components";
import { DeleteNotesButton } from "@/features/NotesManager/Components/DeleteNotesButton";
import type { CreateNoteRequest, UpdateNoteRequest } from "@/services/api/entities/note/types";
import type { Note, Person } from "@/services/api";
import type { NoteEditorFormValues } from "@/features/NoteEditor/model";
import type { MaybeType } from "@/shared/types";
import { TableCardMobile } from "../../TableCardMobile";
import {
  mobileNoteEditorSliceActions,
  mobileNoteEditorSliceSelectors,
} from "../model/mobile-note-editor-slice";
import styles from "./styles.module.scss";

interface Props {
  noteType?: Note["noteType"]["name"];
  widget?: boolean;
  filterQuery?: string;
  currentPerson?: MaybeType<Person>;
}

export const NotesTableMobile: FC<Props> = ({
  noteType = DEFAULT_NOTE_TYPES.DEFAULT_NOTE,
  widget,
  currentPerson,
  filterQuery = "",
}) => {
  const { t } = useTranslation();

  const currentNote = useAppSelector(mobileNoteEditorSliceSelectors.getCurrentNote);
  const dispatch = useAppDispatch();

  const [addNote, setAddNote] = useState(false);
  const edit = useAppSelector(mobileNoteEditorSliceSelectors.getEdit);

  const { data: personsResponse } = useGetAllPersonsQuery();
  const [updateNote] = useUpdateNoteMutation();
  const [createNote] = useCreateNoteMutation();

  const { state, methods } = useNotesTable(noteType, undefined, currentPerson ?? undefined);
  const { dataSource, isLoading, selectedTableNotes, deleteModalIsOpen } = state;
  const { deleteSelected, handleCloseModal, handleOpenModal } = methods;

  const upsertNote = useCallback((data: NoteEditorFormValues["currentNote"]) => {
    const isTempNote = data.id?.includes("new-");
    const payload = isTempNote ? prepareCreateNotePayload(data) : prepareUpdateNotePayload(data);
    const promise = isTempNote
      ? createNote(payload as CreateNoteRequest)
      : updateNote(payload as UpdateNoteRequest);

    promise.unwrap().then((res) => {
      const person = personsResponse?.persons.find((pers) => pers.id === res.personId);

      if (isTempNote) {
        dispatch(
          mobileNoteEditorSliceActions.setNote({
            ...(currentNote ?? createNewTempNote(noteType, currentPerson ?? undefined)),
            id: res.id,
          }),
        );
      }
      dispatch(mobileNoteEditorSliceActions.setNote({ ...res, person: person ?? null }));
    });
  }, []);

  const { tabsManagerMethods } = useTabsManager();
  const { deleteNoteFileById } = tabsManagerMethods;

  if (isLoading) return <>{t("common.loading")}</>;

  const filteredDataSource = !filterQuery
    ? dataSource
    : dataSource.filter((item) => {
        return item.title?.includes(filterQuery) || item.text?.includes(filterQuery);
      });

  return (
    <>
      {edit && (
        <div
          className={styles["arrow-header"]}
          onClick={() => dispatch(mobileNoteEditorSliceActions.setEdit(false))}
        >
          <MobileHeaderTitle backArrow={true} />
        </div>
      )}
      {!widget && (
        <div className={styles["delete-button"]}>
          <DeleteNotesButton
            onClick={() => {
              handleOpenModal();
            }}
            disabled={!selectedTableNotes.length}
          />
        </div>
      )}
      <div className={classNames({ [styles["editor-open"]]: edit || addNote })}>
        {filteredDataSource.map((item) => {
          return (
            <TableCardMobile
              key={item.id}
              currentNote={currentNote ?? item}
              upsertNote={upsertNote}
              deleteNote={deleteSelected}
              noteItem={item}
              content={getTableCardMobileContent({ note: item, person: item.person ?? undefined })}
              settings={{
                isOptions: !widget,
                isEdit: edit,
                isShowFlag: item.noteType.name === DEFAULT_NOTE_TYPES.DEFAULT_COMMITMENT,
              }}
              status={item.status}
              noteType={item.noteType.name}
              important={item.important ?? false}
              deleteNoteFileById={deleteNoteFileById}
              widget={widget}
            />
          );
        })}
      </div>
      {!widget && (
        <div className={styles["add-button"]}>
          <AddNoteButton
            onClick={() => {
              dispatch(
                mobileNoteEditorSliceActions.setNote({
                  ...createNewTempNote(noteType, currentPerson ?? undefined),
                  title: t("notesPage.newNote"),
                }),
              );
              setAddNote(true);
              window.scrollTo(0, 0);
            }}
          />
        </div>
      )}
      <AppDrawer
        placement="bottom"
        open={addNote}
        closable={false}
        height={`${window.innerHeight - 70}px`}
        onClose={() => {
          setAddNote(false);
        }}
        rootClassName={styles.drawer}
        autoFocus
        destroyOnClose
      >
        <NoteEditorMobile
          currentNote={
            currentNote ?? {
              ...createNewTempNote(noteType, currentPerson ?? undefined),
              title: t("notesPage.newNote"),
            }
          }
          noteType={currentNote?.noteType.name ?? noteType}
          onSubmit={upsertNote}
          deleteNoteFileById={deleteNoteFileById}
        />
      </AppDrawer>
      <DeleteNotesModal
        notes={selectedTableNotes}
        onConfirm={deleteSelected}
        onCancel={handleCloseModal}
        open={deleteModalIsOpen}
        noteType={noteType}
      />
    </>
  );
};
